"use client"

import { useCallback, useMemo, useState } from "react"
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  useField,
  useForm,
  useFormFields,
  RenderFields,
} from "@payloadcms/ui"
import type { ArrayFieldClientComponent } from "payload"
import {
  ChevronDown,
  Copy,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Array field.
 *
 * Rebuilds the chrome (row cards, add/remove/duplicate, drag-reorder)
 * using our shadcn primitives + @dnd-kit, while keeping Payload's form
 * state engine (`useField({ hasRows: true })`, `dispatchFields`,
 * `<RenderFields>`) as the data bridge. Row-internal fields are
 * rendered through <RenderFields> which automatically picks up the
 * shadcn overrides we injected via `withShadcnFields`, so arrays of
 * nested text/select/relationship/upload fields also render with
 * shadcn inputs.
 *
 * Features:
 *   • drag-to-reorder rows (GripVertical handle on the left)
 *   • add-row button at the bottom (dispatches ADD_ROW)
 *   • duplicate / remove per row (dispatches DUPLICATE_ROW / REMOVE_ROW)
 *   • collapsible row bodies — click the header to toggle
 *   • row counter in the header so editors always know what index
 *     they're editing even when the row is collapsed
 *   • honours `field.labels.singular` for "Ajouter un X" labels
 *
 * Intentionally NOT supported here (falls back to Payload's own
 * component via a `hasMany === false`-style skip if we can't render):
 *   • `minRows` / `maxRows` enforcement UI — we do pass those to the
 *     underlying dispatchFields but don't add row-count validators on
 *     top of Payload's own.
 *   • Undo/redo of row ops. Payload's built-in undo from ctrl+z on the
 *     form-level state still works though.
 */

export const ShadcnArrayField: ArrayFieldClientComponent = (props) => {
  const {
    path: pathFromProps,
    field,
    schemaPath,
    indexPath: indexPathFromProps,
    permissions,
    readOnly,
  } = props
  const fieldName = (field as { name?: string })?.name ?? pathFromProps
  const path = pathFromProps ?? fieldName
  const indexPath = indexPathFromProps ?? ""

  const { rows, showError, errorMessage } = useField({
    path,
    hasRows: true,
  })

  const { dispatchFields } = useForm()
  const dispatch = dispatchFields as unknown as (action: unknown) => void

  // Child field config — Payload passes the sanitized client fields
  // as `field.fields` on the server-side config. At runtime the
  // useField rowsInitState knows each row's subFieldState.
  const childFields = ((field as { fields?: unknown }).fields ??
    []) as Parameters<typeof RenderFields>[0]["fields"]

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)

  type LabelsConfig = {
    singular?: unknown
    plural?: unknown
  }
  const labels = (field as { labels?: LabelsConfig }).labels ?? {}
  const singularLabel = resolveLabel(labels.singular, label)
  const pluralLabel = resolveLabel(labels.plural, `${label}s`)

  // We keep our own collapse state alongside Payload's so users can
  // fold long rows while editing. Defaults all open.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggleRow = useCallback((id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleAdd = () => {
    dispatch({ type: "ADD_ROW", path })
  }
  const handleRemove = (rowIndex: number) => {
    dispatch({ type: "REMOVE_ROW", path, rowIndex })
  }
  const handleDuplicate = (rowIndex: number) => {
    dispatch({ type: "DUPLICATE_ROW", path, rowIndex })
  }
  const handleMove = (moveFromIndex: number, moveToIndex: number) => {
    if (moveFromIndex === moveToIndex) return
    dispatch({ type: "MOVE_ROW", path, moveFromIndex, moveToIndex })
  }

  // @dnd-kit sensors: only activate drag after a small pointer
  // displacement so single clicks on the header (used to toggle
  // collapse) don't accidentally start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const rowIds = useMemo(
    () => rows?.map((r) => r.id) ?? [],
    [rows],
  )

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = rowIds.indexOf(String(active.id))
    const to = rowIds.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    handleMove(from, to)
  }

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
    >
      {rows && rows.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={rowIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-2">
              {rows.map((row, rowIndex) => (
                <SortableRow
                  key={row.id}
                  id={row.id}
                  rowIndex={rowIndex}
                  singularLabel={singularLabel}
                  collapsed={Boolean(collapsed[row.id])}
                  onToggle={() => toggleRow(row.id)}
                  onRemove={() => handleRemove(rowIndex)}
                  onDuplicate={() => handleDuplicate(rowIndex)}
                  readOnly={Boolean(readOnly)}
                  childFields={childFields}
                  parentPath={`${path}.${rowIndex}`}
                  parentSchemaPath={`${schemaPath}`}
                  parentIndexPath={`${indexPath}`}
                  permissions={permissions as Parameters<typeof RenderFields>[0]["permissions"]}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
          Aucun(e) {singularLabel.toLowerCase()} pour le moment.
        </div>
      )}

      {!readOnly ? (
        <div className="flex justify-start pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            className="h-9 gap-2 rounded-xl"
          >
            <Plus className="size-3.5" />
            <span>Ajouter un(e) {singularLabel.toLowerCase()}</span>
          </Button>
        </div>
      ) : null}
    </FieldShell>
  )
}

type SortableRowProps = {
  id: string
  rowIndex: number
  singularLabel: string
  collapsed: boolean
  onToggle: () => void
  onRemove: () => void
  onDuplicate: () => void
  readOnly: boolean
  childFields: Parameters<typeof RenderFields>[0]["fields"]
  parentPath: string
  parentSchemaPath: string
  parentIndexPath: string
  permissions: Parameters<typeof RenderFields>[0]["permissions"]
}

function SortableRow(p: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: p.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as React.CSSProperties

  // Subscribe to the first row descendant so we can display a quick
  // summary in the collapsed header (e.g. the "key" field of a specs
  // array row). If none resolves, we fall back to the row index.
  const summary = useFirstRowSummary(p.parentPath)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-xl border border-border bg-card transition-shadow",
        isDragging && "opacity-80 shadow-lg",
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Réordonner"
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground cursor-grab active:cursor-grabbing"
          disabled={p.readOnly}
        >
          <GripVertical className="size-4" />
        </button>
        <button
          type="button"
          onClick={p.onToggle}
          className="flex flex-1 items-center gap-2 text-left"
          aria-expanded={!p.collapsed}
        >
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground transition-transform",
              p.collapsed && "-rotate-90",
            )}
          />
          <span className="inline-flex size-5 items-center justify-center rounded-md bg-muted text-[10px] font-bold tabular-nums text-muted-foreground">
            {p.rowIndex + 1}
          </span>
          <span className="text-sm font-medium text-foreground">
            {p.singularLabel}
          </span>
          {p.collapsed && summary ? (
            <span className="ml-2 truncate text-xs text-muted-foreground">
              — {summary}
            </span>
          ) : null}
        </button>
        {!p.readOnly ? (
          <>
            <button
              type="button"
              onClick={p.onDuplicate}
              aria-label="Dupliquer"
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Copy className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={p.onRemove}
              aria-label="Supprimer"
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="size-3.5" />
            </button>
          </>
        ) : null}
      </div>
      {!p.collapsed ? (
        <div className="px-4 py-3">
          {/*
            <RenderFields> is Payload's own recursive field renderer.
            It consumes the `fields` config + a parent path and renders
            each child through the registered component — which means
            any shadcn overrides we attached via `withShadcnFields`
            automatically apply to nested fields inside this array row.
          */}
          <RenderFields
            fields={p.childFields}
            parentPath={p.parentPath}
            parentSchemaPath={p.parentSchemaPath}
            parentIndexPath={p.parentIndexPath}
            permissions={p.permissions}
            readOnly={p.readOnly}
          />
        </div>
      ) : null}
    </li>
  )
}

/**
 * Return a short textual summary of the first child field of the row
 * at `parentPath` so the collapsed header shows something useful
 * (e.g. the "key" of a specs row, the label of a nav link). Falls
 * back to null when the first field value is empty / not text-like.
 */
function useFirstRowSummary(parentPath: string): string | null {
  return useFormFields(([fields]) => {
    if (!fields) return null
    const prefix = `${parentPath}.`
    for (const [key, f] of Object.entries(fields)) {
      if (!key.startsWith(prefix)) continue
      const tail = key.slice(prefix.length)
      if (tail.includes(".")) continue
      const val = (f as { value?: unknown }).value
      if (typeof val === "string" && val.trim()) {
        return val.trim().slice(0, 60)
      }
    }
    return null
  })
}

export default ShadcnArrayField
